import React, { useEffect, useMemo, useState } from 'react';
import {
    AuditOutlined,
    BankOutlined,
    CheckCircleOutlined,
    DashboardOutlined,
    FileProtectOutlined,
    GlobalOutlined,
    SafetyCertificateOutlined,
    SettingOutlined,
    TeamOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { Alert, Button, Card, Col, List, Row, Space, Spin, Statistic, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { getPropertyList } from '../apiCalls';
import { isSuperAdmin } from '../utils/roleUtils';

const controlAreas = [
    {
        title: 'Property and tenant controls',
        detail: 'Manage installer properties, branding, calculator defaults, and feature access.',
        icon: <BankOutlined />,
        path: '/property-manager',
        action: 'Manage properties',
    },
    {
        title: 'Team access',
        detail: 'Provision users with the least privilege and assigned property scope.',
        icon: <TeamOutlined />,
        path: '/create-user',
        action: 'Add team member',
    },
    {
        title: 'Quote governance',
        detail: 'Review pipeline performance and investigate operational activity before taking action.',
        icon: <DashboardOutlined />,
        path: '/dashboard',
        action: 'Open dashboard',
    },
    {
        title: 'Configuration safeguards',
        detail: 'Review calculator rules, pricing inputs, and customer-facing PDF content before publishing.',
        icon: <SettingOutlined />,
        path: '/property-manager',
        action: 'Review configuration',
    },
    {
        title: 'Integration reliability',
        detail: 'Use audit evidence and email configuration to investigate delivery and integration issues.',
        icon: <GlobalOutlined />,
        path: '/audit-log',
        action: 'Review activity',
    },
    {
        title: 'Privacy and retention',
        detail: 'Review access events and keep customer data handling accountable.',
        icon: <FileProtectOutlined />,
        path: '/audit-log',
        action: 'Open audit log',
    },
];

export default function AdminControlCentre() {
    const pageLayoutProps = usePageLayout();
    const { role } = pageLayoutProps;
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        getPropertyList()
            .then((response) => {
                if (!cancelled) setProperties(Array.isArray(response) ? response : []);
            })
            .catch(() => {
                if (!cancelled) setError('Property status could not be loaded. Use Property Manager to retry.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const onboarding = useMemo(() => [
        { title: 'Create and configure a property', complete: properties.length > 0, path: '/property-manager' },
        { title: 'Set pricing and calculation rules', complete: false, path: '/property-manager' },
        { title: 'Configure customer communications', complete: false, path: '/property-manager' },
        { title: 'Run a calculator preview', complete: false, path: '/property-manager' },
    ], [properties.length]);

    if (!isSuperAdmin(role)) {
        return (
            <PageLayout {...pageLayoutProps}>
                <Typography.Title level={4}>Admin control centre</Typography.Title>
                <Typography.Text type='secondary'>Super-admin access is required.</Typography.Text>
            </PageLayout>
        );
    }

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 1280, margin: '0 auto' }}>
                <Space direction='vertical' size={6} style={{ marginBottom: 24 }}>
                    <Typography.Text type='secondary'>Platform administration</Typography.Text>
                    <Typography.Title level={2} style={{ margin: 0 }}>Control centre</Typography.Title>
                    <Typography.Paragraph type='secondary' style={{ maxWidth: 720, margin: 0 }}>
                        A focused view of the controls needed to keep installer properties, customer proposals, and operational access safe.
                    </Typography.Paragraph>
                </Space>

                {error ? <Alert type='warning' showIcon message={error} style={{ marginBottom: 16 }} /> : null}

                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} md={8}>
                        <Card>
                            <Statistic title='Configured properties' value={loading ? 0 : properties.length} prefix={<BankOutlined />} />
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card>
                            <Statistic title='Onboarding steps remaining' value={onboarding.filter((step) => !step.complete).length} prefix={<SafetyCertificateOutlined />} />
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card>
                            <Statistic title='Security posture' value='Review required' prefix={<WarningOutlined />} valueStyle={{ fontSize: 24 }} />
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    {controlAreas.map((area) => (
                        <Col xs={24} md={12} lg={8} key={area.title}>
                            <Card style={{ height: '100%' }}>
                                <Space align='start' size={12}>
                                    <div style={{ color: '#1f7a8c', fontSize: 22 }}>{area.icon}</div>
                                    <div>
                                        <Typography.Title level={4}>{area.title}</Typography.Title>
                                        <Typography.Paragraph type='secondary'>{area.detail}</Typography.Paragraph>
                                        <Button type='primary' ghost onClick={() => navigate(area.path)}>{area.action}</Button>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Card title='Safe onboarding checklist' style={{ marginTop: 24 }}>
                    {loading ? <Spin /> : (
                        <List
                            dataSource={onboarding}
                            renderItem={(step) => (
                                <List.Item
                                    actions={[<Button key={step.title} type='link' onClick={() => navigate(step.path)}>Open</Button>]}
                                >
                                    <Space>
                                        {step.complete ? <CheckCircleOutlined style={{ color: '#2f9e44' }} /> : <SafetyCertificateOutlined />}
                                        <span>{step.title}</span>
                                        <Tag color={step.complete ? 'success' : 'default'}>{step.complete ? 'Complete' : 'Required'}</Tag>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    )}
                </Card>

                <Alert
                    style={{ marginTop: 24 }}
                    type='info'
                    showIcon
                    icon={<AuditOutlined />}
                    message='Governance default'
                    description='Use the audit log before changing customer-facing configuration or user access. Secrets and customer portal links are intentionally not displayed in this control centre.'
                />
            </div>
        </PageLayout>
    );
}
